import { primaryEnvironmentFetch } from "../environments/primary/fetch";
import { hostNativeComposerAttachment, type ComposerPathAttachment } from "./composerAttachments";

export const HOST_PICK_ATTACHMENTS_PATH = "/api/host/pick-attachments";

type HostPickAttachmentsResponse = {
  readonly attachments?: ReadonlyArray<{
    readonly type: "file" | "folder";
    readonly name: string;
    readonly path: string;
  }>;
  readonly error?: string;
};

/**
 * Host Web / browser path: ask the Environment Service to open one native
 * macOS panel that can select files and folders together.
 *
 * Call from the Add menu via async `fetch` (this function). Do not use sync
 * XHR — that freezes the page while NSOpenPanel is open. Do not fall back to
 * `input.click()` after this await — Chromium suppresses that click.
 */
export async function pickHostNativeComposerAttachments(
  makeId: () => string,
  fetchFn: typeof globalThis.fetch = globalThis.fetch,
): Promise<ReadonlyArray<ComposerPathAttachment> | null> {
  let response: Response;
  try {
    response = await primaryEnvironmentFetch(
      HOST_PICK_ATTACHMENTS_PATH,
      { method: "POST", headers: { accept: "application/json" } },
      fetchFn,
    );
  } catch {
    return null;
  }

  if (response.status === 501) {
    return null;
  }
  if (!response.ok) {
    return null;
  }

  const body = (await response.json()) as HostPickAttachmentsResponse;
  const attachments = body.attachments ?? [];
  return attachments.map((attachment) =>
    hostNativeComposerAttachment(makeId(), {
      type: attachment.type,
      name: attachment.name,
      path: attachment.path,
    }),
  );
}
